import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarPlus, Megaphone, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import NowModal from "./NowModal";
import { ReadOnlyNote } from "@/components/app/AppShell";
import { api, cardCls } from "@/lib/api";

const BillingBanner = ({ readOnly }) => {
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    api("/billing/status").then((d) => setBilling(d.billing)).catch(() => {});
  }, []);

  if (!billing) return null;

  const ctaCls = "ml-auto shrink-0 rounded-lg bg-guinep-deep px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-guinep-mid";
  let content = null;

  if (billing.status === "trial" && billing.trial_days_remaining > 3) {
    content = (
      <>
        <p className="text-sm text-guinep-ink"><span className="font-semibold">{billing.trial_days_remaining} days left</span> in your free trial</p>
        {!readOnly && <Link to="/subscribe" data-testid="trial-subscribe-cta" className={ctaCls}>Subscribe now</Link>}
      </>
    );
  } else if (billing.status === "trial") {
    content = (
      <>
        <p className="text-sm text-guinep-ink">Your trial ends in <span className="font-semibold">{billing.trial_days_remaining} {billing.trial_days_remaining === 1 ? "day" : "days"}</span> — keep your Guinep Seed page live for $9 for your first month.</p>
        {!readOnly && <Link to="/subscribe" data-testid="trial-subscribe-cta" className={ctaCls}>Subscribe now</Link>}
      </>
    );
  } else if (billing.status === "expired") {
    content = (
      <>
        <div>
          <p className="text-sm font-semibold text-guinep-ink">Your free trial has ended.</p>
          <p className="mt-0.5 text-xs text-guinep-mute">Subscribe to keep your Guinep Seed page live.</p>
        </div>
        {!readOnly && <Link to="/subscribe" data-testid="trial-subscribe-cta" className={ctaCls}>Subscribe — $9 first month</Link>}
      </>
    );
  } else if (billing.status === "past_due") {
    content = (
      <>
        <p className="text-sm font-semibold text-guinep-ink">We couldn't process your subscription payment.</p>
        {!readOnly && <Link to="/subscribe" data-testid="trial-subscribe-cta" className={ctaCls}>Update payment</Link>}
      </>
    );
  } else if (billing.status === "cancelled" && billing.current_period_end) {
    content = (
      <p className="text-sm text-guinep-ink">
        Your subscription is cancelled — your page stays live until{" "}
        <span className="font-semibold">{new Date(billing.current_period_end).toLocaleDateString("en-US", { month: "long", day: "numeric" })}</span>.
      </p>
    );
  }

  if (!content) return null;
  return (
    <div className="mt-5 flex items-center gap-3 rounded-2xl border border-guinep-line/70 bg-white px-4 py-3.5" data-testid="billing-banner">
      {content}
    </div>
  );
};

const AppointmentSummary = ({ onView }) => {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api("/appointments/summary").then(setSummary).catch(() => {});
  }, []);

  if (!summary) return null;

  return (
    <div className="mt-4 rounded-2xl border border-guinep-line/70 bg-white px-4 py-3.5" data-testid="appointment-summary">
      {summary.today_count === 0 && summary.awaiting_count === 0 && !summary.next ? (
        <div className="flex items-center justify-between">
          <p className="text-sm text-guinep-mute">No appointments today</p>
          <button onClick={onView} data-testid="view-calendar-link" className="text-xs font-semibold text-guinep-mid hover:text-guinep-deep">View calendar</button>
        </div>
      ) : (
        <div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="text-guinep-ink">Today: <span className="font-semibold">{summary.today_count} {summary.today_count === 1 ? "appointment" : "appointments"}</span></span>
            {summary.awaiting_count > 0 && (
              <span className="text-amber-700">Needs attention: <span className="font-semibold">{summary.awaiting_count} awaiting confirmation</span></span>
            )}
          </div>
          {summary.next && (
            <p className="mt-1 text-xs text-guinep-mute">Next: <span className="font-medium text-guinep-ink">{summary.next.time || "Upcoming"} — {summary.next.service_title}</span></p>
          )}
          <button onClick={onView} data-testid="view-calendar-link" className="mt-1.5 text-xs font-semibold text-guinep-mid hover:text-guinep-deep">View calendar</button>
        </div>
      )}
    </div>
  );
};

const HomeTab = ({ user, business, services, reload, goCalendar, readOnly = false }) => {
  const [modal, setModal] = useState(null);
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const guard = (fn) => (readOnly ? () => toast.info("Admin view — read only") : fn);

  const rows = [
    ["Page status", business.is_live ? "Live" : "Draft"],
    ["Taking appointments", business.taking_appointments ? "Yes" : "No"],
    ["Active services", String(services.length)],
    ["WhatsApp connected", business.whatsapp_number ? "Yes" : "No"],
  ];

  return (
    <div data-testid="home-tab">
      <h1 className="font-serif text-3xl font-medium tracking-tight text-guinep-ink">
        {greeting}, {business.owner_first_name || user?.name || "there"}
      </h1>
      {readOnly && <ReadOnlyNote />}
      <BillingBanner readOnly={readOnly} />
      <AppointmentSummary onView={goCalendar} />
      <p className="mt-5 font-serif text-lg text-guinep-ink">What changed in your business today?</p>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <button data-testid="add-availability-update" onClick={guard(() => setModal("availability"))} className={`${cardCls} flex flex-col items-start gap-2.5 p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-sm`}>
          <CalendarPlus size={18} className="text-guinep-mid" />
          <span className="text-[13px] font-semibold text-guinep-ink">Add availability update</span>
          <span className="text-[11px] leading-snug text-guinep-mute">Openings, walk-ins, fully booked days</span>
        </button>
        <button data-testid="add-business-notice" onClick={guard(() => setModal("general_notice"))} className={`${cardCls} flex flex-col items-start gap-2.5 p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-sm`}>
          <Megaphone size={18} className="text-guinep-mid" />
          <span className="text-[13px] font-semibold text-guinep-ink">Add business notice</span>
          <span className="text-[11px] leading-snug text-guinep-mute">Hours, closures, location or service changes</span>
        </button>
      </div>

      <p className="mt-8 font-serif text-lg text-guinep-ink">Your business right now</p>
      <div className={`${cardCls} mt-3 divide-y divide-guinep-line/60`} data-testid="business-status-card">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between px-4 py-3.5">
            <span className="text-sm text-guinep-mute">{k}</span>
            <span className="text-sm font-semibold text-guinep-ink">{v}</span>
          </div>
        ))}
      </div>

      <a
        href={`/${business.slug}`}
        target="_blank"
        rel="noreferrer"
        data-testid="view-public-page-button"
        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-guinep-deep py-3.5 text-sm font-semibold text-white transition-colors hover:bg-guinep-mid"
      >
        <ExternalLink size={15} /> View my public page
      </a>

      <NowModal open={modal !== null} presetType={modal || "availability"} onClose={() => setModal(null)} onSaved={reload} />
    </div>
  );
};

export default HomeTab;
